import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import collectionsBannerImgSrc from '../../../assets/images/appsStoreCollectionsBanner.jpg';
import {ApplicationCategoryCard} from '../../../components/common/applications/applicationCategoryCard';
import {ApplicationCardSkeleton} from '../../../components/common/applications/skeleton/applicationCardSkeleton';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {
  renderIntegrationTypeCategoryDescription,
  renderIntegrationTypeCategoryName,
} from '../../../helpers/integrationTypesHelpers';
import {IntegrationTypeCategoriesEnum, IntegrationTypeModel} from '../../../models/api/integrationTypesModel';
import {buildSettingsAppsCategoryUrl, buildSettingsAppsTabUrl} from '../../../models/url/settingsUrlHelpers';
import {SettingsAppsCollectionsUrl, SettingsAppsTabsEnum} from '../../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {AppsStoreBanner} from '../banner/appsStoreBanner';
import {AppsGrid} from '../layout/appsGrid';
import {SettingsAppCardContainer} from '../settingsAppCardContainer';

/*
 * Constants.
 */

const maxIntegrationsToRender = 4;
const categoriesToRender = [
  IntegrationTypeCategoriesEnum.PROJECT_MANAGEMENT_ISSUE_TRACKING,
  IntegrationTypeCategoriesEnum.ANALYTICS_REPORTING,
  IntegrationTypeCategoriesEnum.DATA_SYNC_ENRICHMENT,
  IntegrationTypeCategoriesEnum.CRM_ECOMMERCE,
];

/*
 * Props.
 */

interface SettingsAppsCollectionsPageProps {
  popularIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  popularPluginIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  popularChannelIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  recentlyAddedIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  isIntegrationTypesLoading: boolean;
  url: SettingsAppsCollectionsUrl;
}

/*
 * Component.
 */

export const SettingsAppsCollectionsPage: FC<SettingsAppsCollectionsPageProps> = (props) => {
  const {
    popularIntegrationTypes,
    popularPluginIntegrationTypes,
    popularChannelIntegrationTypes,
    recentlyAddedIntegrationTypes,
    isIntegrationTypesLoading,
    url,
  } = props;
  const {navigateToLink} = useLinkNavigation();

  const onCategoryClick = (category: IntegrationTypeCategoriesEnum) => {
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {
      id: 'see-apps',
      category,
    });
    navigateToLink(
      buildSettingsAppsCategoryUrl(category, buildSettingsAppsTabUrl(url.tabPage, url.searchQuery)),
    );
  };

  return (
    <>
      <AppsStoreBanner
        backgroundUrl={collectionsBannerImgSrc}
        title={<Trans id="settings-apps-collections-banner-title">Your favorite apps, right in Front</Trans>}
        description={
          <Trans id="settings-apps-collections-banner-description">
            Powerful plugins, channels, workflows and more to increase team efficiency, reach more customers,
            and deliver the very best customer experience.
          </Trans>
        }
      />
      <AppsGrid
        title={<Trans id="settings-apps-collections-categories-title">Categories</Trans>}
        description={
          <Trans id="settings-apps-collections-categories-description">
            Discover apps based on these most popular use cases.
          </Trans>
        }
        action={
          <Button
            variant={ButtonVariantsEnum.LINK}
            onClick={() => {
              trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: 'see-all-categories'});
              navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.CATEGORIES));
            }}
          >
            <Trans id="settings-apps-collections-categories-action">See all categories</Trans>
          </Button>
        }
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderLoadingState}
        shouldHideOverflowingItems
      >
        {categoriesToRender.map((category) => (
          <ApplicationCategoryCard
            key={category}
            title={renderIntegrationTypeCategoryName(category)}
            description={renderIntegrationTypeCategoryDescription(category)}
          >
            <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => onCategoryClick(category)}>
              <Trans id="settings-apps-collections-category-card-button">See apps</Trans>
            </Button>
          </ApplicationCategoryCard>
        ))}
      </AppsGrid>
      <AppsGrid
        title={<Trans id="settings-apps-collections-popular-title">Popular apps you might like</Trans>}
        description={
          <Trans id="settings-apps-collections-popular-description">
            Browse through the most commonly enabled plugins in Front.
          </Trans>
        }
        action={
          <Button
            variant={ButtonVariantsEnum.LINK}
            onClick={() => {
              trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: 'see-all-popular-apps'});
              navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.POPULAR));
            }}
          >
            <Trans id="settings-apps-collections-popular-action">See all popular apps</Trans>
          </Button>
        }
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderLoadingState}
        shouldHideOverflowingItems
      >
        {popularIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
      <AppsGrid
        title={
          <Trans id="settings-apps-collections-popular-plugins-title">
            Embed your favorite tools in Front with Plugins
          </Trans>
        }
        description={
          <Trans id="settings-apps-collections-popular-plugins-description">
            View important information about contacts, keep track of tasks, start video calls and more all
            directly in Front.
          </Trans>
        }
        action={
          <Button
            variant={ButtonVariantsEnum.LINK}
            onClick={() => navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.PLUGINS))}
          >
            <Trans id="settings-apps-collections-popular-plugins-action">See all plugin apps</Trans>
          </Button>
        }
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderLoadingState}
        shouldHideOverflowingItems
      >
        {popularPluginIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
      <AppsGrid
        title={
          <Trans id="settings-apps-collections-popular-channels-title">
            Communicate through different channels all from Front
          </Trans>
        }
        description={
          <Trans id="settings-apps-collections-popular-channels-description">
            Consolidate all of your different communication channels directly into Front.
          </Trans>
        }
        action={
          <Button
            variant={ButtonVariantsEnum.LINK}
            onClick={() => {
              trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_CLICK, {id: 'see-all-channels'});
              navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.CHANNELS));
            }}
          >
            <Trans id="settings-apps-collections-popular-channels-action">See all channel apps</Trans>
          </Button>
        }
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderLoadingState}
        shouldHideOverflowingItems
      >
        {popularChannelIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
      <AppsGrid
        title={<Trans id="settings-apps-collections-recently-added-title">Recently added</Trans>}
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderLoadingState}
        shouldHideOverflowingItems
      >
        {recentlyAddedIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
    </>
  );
};

/*
 * Helpers.
 */

function renderLoadingState() {
  return (
    <>
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
    </>
  );
}
